import { HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { compact, sortBy } from 'lodash-es';
import { BehaviorSubject, Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, concatMap, defaultIfEmpty, exhaustMap, map, switchMap, take, tap } from 'rxjs/operators';
import { v4 as uuidv4, v5 as uuidv5 } from 'uuid';

import { environment } from '@env';
import { constants } from '@shared/constants';
import { CustomHttpParamEncoder } from '@shared/encoder';
import { SKIP_ERROR_NOTIFICATION } from '@shared/interceptors';
import {
  BusinessAccount,
  CounterpartyRaw,
  CustomerDetailRaw,
  EnabledSolutionConfig,
  EnabledSolutionPermissions,
  EnabledSolutionsRaw,
  FilterValues,
  MCSendTransactionDetailsRaw,
  MoveMoneyRequest,
  MultiLegPaymentReason,
  MultiLegPaymentReasonRaw,
  PaymentReason,
  SingleLegTransactionFinancialAccount,
  TeamMember,
  TransactionAnanlytics,
  TransactionDetails,
  TransactionDetailsRaw,
  TransactionDirection,
  TransactionEditRequestData,
  TransactionHistoryList,
  TransactionHistoryListResponse,
  TransactionHistoryRequest,
  TransactionList,
  TransactionListRaw,
  TransactionListRequest,
  TransactionReversalRaw,
  TransactionReverseRequestData,
} from '@shared/models';
import { BusinessAccountService, CustomerService, FinancialAccountService, StorageService, TeamMemberService } from '@shared/services';
import { BackendService } from '@shared/services/backend.service';
import { MultiLegTransactionsService } from '@shared/services/transaction/multi-leg-transactions.service';
import { AuditActions, fromAuth } from '@shared/store';
import { systemTimeZone } from '@shared/utils';

import {
  mapEnabledSolutionsData,
  mapFinancialAccountHolderInfo,
  mapFinancialAccountInfo,
  mapMCSendTransactionDetails,
  mapTransactionDetails,
  mapTransactionHistoryData,
  mapTransactionListData,
  transactionsRequestParams,
} from './transaction-mapping-utils';

@Injectable({
  providedIn: 'root',
})
export class TransactionService {
  transactionsBasePath = `${environment.transactionFlowEndpoint}/business-accounts`;

  activeTransactionFilters = new BehaviorSubject<FilterValues>({});

  batchTransactionActivityFilters = new BehaviorSubject<FilterValues>({});

  teamMembersName: Record<string, TeamMember['name'] | undefined> = {};

  multiLegTransactionsService = inject(MultiLegTransactionsService);

  constructor(
    private businessAccountService: BusinessAccountService,
    private teamMemberService: TeamMemberService,
    private customerService: CustomerService,
    private backendService: BackendService,
    private storageService: StorageService,
    private financialAccountService: FinancialAccountService,
    private store: Store,
  ) {}

  private getSelectedBusinessAccountId(): Observable<BusinessAccount['id']> {
    return this.businessAccountService.getSelectedBusinessAccountId();
  }

  /**
   * Retrieves a list of transactions from the backend API for the selected business account based on the provided request parameters.
   * API {{base-url}}/transactionflow/api/portal/v1/business-accounts/{{ba-id}}/transactions
   *
   * @param {TransactionListRequest} requestParams - The request parameters object containing pagination, sorting, filtering and batchId.
   * @returns {Observable<TransactionList>} - An Observable that emits a TransactionList object.
   */
  public getTransactions(requestParams: TransactionListRequest): Observable<TransactionList> {
    const params = transactionsRequestParams(requestParams);

    return this.getSelectedBusinessAccountId().pipe(
      switchMap((businessAccountId) =>
        this.backendService.get<TransactionListRaw>(`${this.transactionsBasePath}/${businessAccountId}/transactions`, { params }).pipe(
          map((transactionsData) => mapTransactionListData(transactionsData)),
          catchError((errorRes) => throwError(() => errorRes)),
        ),
      ),
    );
  }

  /**
   * Retrieves a list of transactions from the backend API for the business account based on the provided request parameters.
   * API {{base-url}}/transactionflow/api/portal/v1/business-accounts/{{ba-id}}/transactions
   *
   * @param {string} businessAccountId - Business Account ID
   * @param {TransactionListRequest} requestParams - The request parameters object containing pagination, sorting, filtering and batchId.
   * @returns {Observable<TransactionList>} - An Observable that emits a TransactionList object.
   */
  public getTransactionList(businessAccountId: BusinessAccount['id'], requestParams: TransactionListRequest): Observable<TransactionList> {
    const params = transactionsRequestParams(requestParams);
    return this.backendService.get<TransactionListRaw>(`${this.transactionsBasePath}/${businessAccountId}/transactions`, { params }).pipe(
      map((transactionsData) => mapTransactionListData(transactionsData)),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public getPaymentReasons(type?: TransactionDirection): Observable<PaymentReason[]> {
    let params = new HttpParams({ encoder: new CustomHttpParamEncoder() });

    if (type) {
      params = params.append('type', type.toUpperCase());
    }

    return this.backendService.get<PaymentReason[]>(`${environment.transactionFlowEndpoint}/payment-reasons`, { params }).pipe(
      map((response) => {
        const reasonsList = response ?? [];
        const sortedPaymentReasons = sortBy(reasonsList, 'reason');
        return reasonsList.length ? [...sortedPaymentReasons] : [];
      }),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public createMoveMoneyTransaction(moveMoneyData: MoveMoneyRequest, idempotencyKey?: string): Observable<TransactionDetails> {
    let headers = new HttpHeaders();
    if (idempotencyKey) {
      headers = headers.set('Idempotency-Key', idempotencyKey);
    }

    return this.getSelectedBusinessAccountId().pipe(
      switchMap((businessAccountId) =>
        this.backendService
          .post<TransactionDetailsRaw>(`${this.transactionsBasePath}/${businessAccountId}/transactions`, moveMoneyData, { headers })
          .pipe(
            map((transaction) => mapTransactionDetails(transaction)),
            catchError((errorRes) => throwError(() => errorRes)),
          ),
      ),
    );
  }

  public getTransactionById(businessAccountId: string, transactionId: TransactionDetails['id']): Observable<TransactionDetails> {
    return this.backendService
      .get<TransactionDetailsRaw>(`${this.transactionsBasePath}/${businessAccountId}/transactions/${transactionId}`)
      .pipe(
        exhaustMap((transactionResponse) => {
          return this.getAllPaymentReasons().pipe(
            map((paymentReasons) => ({
              ...transactionResponse,
              paymentReasons,
            })),
          );
        }),
        switchMap((transactionData) => {
          this.store.dispatch(AuditActions.loadTeamMembers({ ids: compact([transactionData.createdBy, transactionData.updatedBy]) }));

          const zonedDate = toZonedTime(transactionData.createdAt, systemTimeZone);
          const formattedZonedDate = format(zonedDate, "yyyy-MM-dd'T'HH:mm:ss.SSSXXX");

          const skipErrorContext = new HttpContext().set(SKIP_ERROR_NOTIFICATION, true);

          const debitFA$ = this.financialAccountService
            .getFinancialAccountRawDetails({
              businessAccountId,
              financialAccountId: transactionData.debitFinancialAccountId,
              isSuppressError: true,
            })
            .pipe(
              switchMap((rawFA) => {
                if (!rawFA || rawFA.state === 'DELETED') {
                  return of({ ...rawFA, detailsWithBalances: null });
                }
                return this.financialAccountService
                  .getFinancialAccountDetailsWithBalances(transactionData.debitFinancialAccountId, skipErrorContext)
                  .pipe(
                    map((details) => ({ ...rawFA, detailsWithBalances: details })),
                    catchError(() => of({ ...rawFA, detailsWithBalances: null })),
                  );
              }),
              catchError(() => of(null)),
            );

          const creditFA$ = this.financialAccountService
            .getFinancialAccountRawDetails({
              businessAccountId,
              financialAccountId: transactionData.creditFinancialAccountId,
              isSuppressError: true,
            })
            .pipe(
              switchMap((rawFA) => {
                if (!rawFA || rawFA.state === 'DELETED') {
                  return of({ ...rawFA, detailsWithBalances: null });
                }
                return this.financialAccountService
                  .getFinancialAccountDetailsWithBalances(transactionData.creditFinancialAccountId, skipErrorContext)
                  .pipe(
                    map((details) => ({ ...rawFA, detailsWithBalances: details })),
                    catchError(() => of({ ...rawFA, detailsWithBalances: null })),
                  );
              }),
              catchError(() => of(null)),
            );

          const debitFinancialAccount$ = this.getFinancialAccountAccountHolder(transactionData.debitFinancialAccount).pipe(
            switchMap((financialAccount) => {
              return of(mapFinancialAccountInfo(financialAccount));
            }),
          );

          const creditFinancialAccount$ = this.getFinancialAccountAccountHolder(transactionData.creditFinancialAccount).pipe(
            switchMap((financialAccount) => {
              return of(mapFinancialAccountInfo(financialAccount));
            }),
          );

          const transactionInitiator$ = transactionData?.initiatorAccountHolderId
            ? this.getSelectedBusinessAccountId().pipe(
                switchMap((accountId) => {
                  if (accountId === transactionData.initiatorAccountHolderId) {
                    return this.store.select(fromAuth.selectBusinessAccountName).pipe(take(1));
                  } else {
                    return this.customerService.getCustomerById(transactionData.initiatorAccountHolderId!).pipe(
                      map((customer) => {
                        return `${customer.firstName} ${customer.lastName}`;
                      }),
                    );
                  }
                }),
              )
            : of(undefined);

          return forkJoin([debitFA$, creditFA$, debitFinancialAccount$, creditFinancialAccount$, transactionInitiator$]).pipe(
            switchMap(([debitFAResult, creditFAResult, debitFinancialAccount, creditFinancialAccount, transactionInitiatorDetails]) => {
              const anyFADeleted =
                !debitFAResult || debitFAResult.state === 'DELETED' || !creditFAResult || creditFAResult.state === 'DELETED';

              const deliverySpeedOptions$ = anyFADeleted
                ? of([])
                : this.multiLegTransactionsService
                    .getDeliverySpeedOptions({
                      debitFinancialAccountId: transactionData.debitFinancialAccountId,
                      creditFinancialAccountId: transactionData.creditFinancialAccountId,
                      dateTime: formattedZonedDate,
                    })
                    .pipe(catchError(() => of([])));

              return deliverySpeedOptions$.pipe(
                map((deliverySpeedOptions) => ({
                  ...transactionData,
                  debitFinancialAccount: {
                    ...debitFinancialAccount,
                    state: debitFAResult?.state ?? 'DELETED',
                  },
                  creditFinancialAccount: {
                    ...creditFinancialAccount,
                    state: creditFAResult?.state ?? 'DELETED',
                  },
                  debitFinancialAccountDetails: debitFAResult?.detailsWithBalances ?? null,
                  creditFinancialAccountDetails: creditFAResult?.detailsWithBalances ?? null,
                  transactionInitiatorDetails,
                  deliverySpeedOptions,
                })),
              );
            }),
          );
        }),
        map((transaction: TransactionDetailsRaw) => mapTransactionDetails(transaction)),
        catchError((errorRes) => throwError(() => errorRes)),
      )
      .pipe(
        concatMap((transactionDetails: TransactionDetails) => {
          if (transactionDetails.solution !== 'push-to-card') {
            return of(transactionDetails);
          }
          return this.backendService
            .get<MCSendTransactionDetailsRaw>(
              `${environment.compositionEndpoint}/business-accounts/${businessAccountId}/transactions/${transactionId}/details`,
            )
            .pipe(
              map((transactionMCSendDetails) => mapMCSendTransactionDetails(transactionDetails, transactionMCSendDetails)),
              catchError(() => of(transactionDetails)),
            );
        }),
      );
  }

  public getSolutionConfigs(solutionNames: string[]): Observable<EnabledSolutionConfig[]> {
    const params = new HttpParams({ encoder: new CustomHttpParamEncoder() }).set('solutionNames', solutionNames.join(','));
    return this.backendService.get<EnabledSolutionConfig[]>(`${environment.transactionFlowEndpoint}/solutions/quick-info`, { params }).pipe(
      map((configs) => configs),
      catchError((errorRes) => {
        return throwError(() => errorRes);
      }),
    );
  }

  public reverseTransaction(
    transactionId: TransactionDetails['id'],
    reverseData: TransactionReverseRequestData,
  ): Observable<TransactionDetails> {
    let headers = new HttpHeaders();

    const idempotencyKey = `idemp-${uuidv4()}`;
    headers = headers.set('Idempotency-Key', idempotencyKey);

    return this.getSelectedBusinessAccountId().pipe(
      switchMap((businessAccountId) =>
        this.backendService
          .post<TransactionReversalRaw>(
            `${this.transactionsBasePath}/${businessAccountId}/transactions/${transactionId}/reverse`,
            reverseData,
            { headers },
          )
          .pipe(
            map((transaction: TransactionReversalRaw) => {
              const { correctionTransaction, reversalTransaction } = transaction;
              return mapTransactionDetails(correctionTransaction ?? reversalTransaction);
            }),
            catchError((errorRes) => throwError(() => errorRes)),
          ),
      ),
    );
  }

  public editTransaction(transactionId: TransactionDetails['id'], editData: TransactionEditRequestData): Observable<TransactionDetails> {
    let headers = new HttpHeaders();
    const transactionDataString = transactionId + JSON.stringify(editData);
    const idempotencyKey = `idemp-${uuidv5(transactionDataString, constants.UUID_NAMESPACE)}`;
    headers = headers.set('Idempotency-Key', idempotencyKey);

    return this.getSelectedBusinessAccountId().pipe(
      switchMap((businessAccountId) =>
        this.backendService
          .update<TransactionDetailsRaw>(`${this.transactionsBasePath}/${businessAccountId}/transactions/${transactionId}`, editData, {
            headers,
          })
          .pipe(
            map((transaction: TransactionDetailsRaw) => mapTransactionDetails(transaction)),
            catchError((errorRes) => throwError(() => errorRes)),
          ),
      ),
    );
  }

  public cancelTransaction(transactionId: TransactionDetails['id']): Observable<boolean> {
    return this.getSelectedBusinessAccountId().pipe(
      switchMap((businessAccountId) =>
        this.backendService.delete<void>(`${this.transactionsBasePath}/${businessAccountId}/transactions/${transactionId}`).pipe(
          map(() => true),
          catchError((errorRes) => throwError(() => errorRes)),
        ),
      ),
    );
  }

  /**
   * Returns Transaction History List
   * API {{base-url}}/transactionflow/api/portal/v1/business-accounts/{{ba-id}}/transaction-histories
   *
   * @param page - page number
   * @param size - page size
   * @param transactionId - transaction id
   * @returns {Observable<TransactionHistoryList>}
   */

  public getTransactionHistory({ transactionId, page, size }: TransactionHistoryRequest): Observable<TransactionHistoryList> {
    const pageSize = size ?? constants.TABLE_ROWS;

    const params = new HttpParams({ encoder: new CustomHttpParamEncoder() })
      .set('page', `${page ?? 0}`)
      .set('pageSize', `${pageSize}`)
      .set('transactionId', transactionId);

    return this.getSelectedBusinessAccountId().pipe(
      switchMap((businessAccountId) =>
        this.backendService
          .get<TransactionHistoryListResponse>(`${this.transactionsBasePath}/${businessAccountId}/transaction-histories`, { params })
          .pipe(
            tap({
              next: (response) => {
                const { content = [] } = response ?? {};
                content
                  .filter((item) => !!item.createdBy && item.createdBy.id !== businessAccountId)
                  .forEach((item) => {
                    if (item.createdBy?.id && !this.teamMembersName[item.createdBy.id]) {
                      this.teamMembersName[item.createdBy.id] = undefined;
                    }
                  });
              },
            }),
            switchMap((transactionHistoryResponse) =>
              forkJoin(
                Object.keys(this.teamMembersName).map((teamMemberId) => {
                  if (Object.keys(this.teamMembersName).includes(teamMemberId) && this.teamMembersName[teamMemberId]) {
                    return of({});
                  }
                  const teamMemberRaw = transactionHistoryResponse.content?.find((item) => item.createdBy?.id === teamMemberId);
                  if (teamMemberRaw?.createdBy?.type === 'internal') {
                    this.teamMembersName[teamMemberId] = undefined;
                    return of({});
                  }
                  return this.teamMemberService.getSubjectsQuickInfo([teamMemberId]).pipe(
                    map(([teamMember]) => {
                      this.teamMembersName[teamMemberId] = teamMember?.name ?? undefined;
                    }),
                    catchError(() => of({})),
                  );
                }),
              ).pipe(
                defaultIfEmpty({}),
                map(() => mapTransactionHistoryData(transactionHistoryResponse, this.teamMembersName)),
              ),
            ),
          ),
      ),
    );
  }

  /**
   * Returns Enabled Solutions List
   * API {{base-url}}/accountpermissions/api/portal/permissions/enabled-solutions
   *
   * @returns {Observable<EnabledSolutionPermissions>}
   */
  public getEnabledSolutions(): Observable<EnabledSolutionPermissions> {
    return this.backendService.get<EnabledSolutionsRaw>(`${environment.accountPermissionEndpoint}/permissions/enabled-solutions`).pipe(
      map((response) => mapEnabledSolutionsData(response)),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public setFilterParams(filters: FilterValues, isBatch?: boolean): void {
    const key = isBatch ? constants.KOR_TRANSACTION_BATCH_ACTIVITY_FILTER : constants.KOR_TRANSACTION_FILTER;
    this.storageService.setItem(key, filters);
    this.updateFilterParams(filters, isBatch);
  }

  public getFilterParams(isBatch?: boolean): void {
    const key = isBatch ? constants.KOR_TRANSACTION_BATCH_ACTIVITY_FILTER : constants.KOR_TRANSACTION_FILTER;
    const filters = this.storageService.getItem<FilterValues>(key);

    if (filters && Object.keys(filters).length) {
      this.updateFilterParams(filters, isBatch);
    }
  }

  private updateFilterParams(filters: FilterValues, isBatch?: boolean): void {
    if (isBatch) {
      this.batchTransactionActivityFilters.next(filters);
    } else {
      this.activeTransactionFilters.next(filters);
    }
  }

  getFinancialAccountAccountHolder(
    financialAccountResponse: SingleLegTransactionFinancialAccount,
  ): Observable<SingleLegTransactionFinancialAccount> {
    const { accountHolderId, accountHolderType, businessAccountId } = financialAccountResponse ?? {};
    if (accountHolderId && accountHolderType === 'COUNTERPARTY') {
      return this.backendService.get<CounterpartyRaw>(`${environment.accountFlowService}/v1/counterparties/${accountHolderId}`).pipe(
        map((recipient) => mapFinancialAccountHolderInfo({ ...financialAccountResponse, recipient })),
        catchError(() => of(financialAccountResponse)),
      );
    } else if (accountHolderId && accountHolderType === 'CUSTOMER') {
      return this.backendService
        .get<CustomerDetailRaw>(`${environment.accountFlowService}/v2/customers/${accountHolderId}`, {
          context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true),
        })
        .pipe(
          map((customer) => mapFinancialAccountHolderInfo({ ...financialAccountResponse, customer })),
          catchError(() => of(financialAccountResponse)),
        );
    } else if (!accountHolderId || accountHolderType === 'BUSINESS_ACCOUNT') {
      return of(true).pipe(
        concatLatestFrom(() => this.store.select(fromAuth.selectActiveBusinessAccountDetails)),
        map(([, activeBusinessAccount]) => {
          if (businessAccountId === activeBusinessAccount.id) {
            return mapFinancialAccountHolderInfo({ ...financialAccountResponse, businessAccount: activeBusinessAccount });
          }

          return financialAccountResponse;
        }),
      );
    } else {
      return of(financialAccountResponse);
    }
  }

  getAllPaymentReasons(): Observable<MultiLegPaymentReason[]> {
    return this.backendService.get<MultiLegPaymentReasonRaw[]>(`${environment.transactionFlowEndpoint}/payment-reasons`).pipe(
      map((response = []) => {
        return sortBy(response, 'reason')
          .filter(({ state, types }) => state === 'ACTIVE' && types?.length)
          .map((reasons) => {
            const { id, reason, allowedSolutions, types } = reasons;
            return { label: reason, value: id, allowedSolutions, types: types! };
          });
      }),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  getTransactionAnalytics({
    businessAccountId,
    period,
    currency,
  }: {
    businessAccountId: string;
    period: string;
    currency: string;
  }): Observable<TransactionAnanlytics> {
    return this.backendService
      .get<TransactionAnanlytics>(
        `${environment.transactionFlowEndpoint}/business-accounts/${businessAccountId}/transactions/analytics?type=${period}&currency=${currency}`,
      )
      .pipe(catchError((errorRes) => throwError(() => errorRes)));
  }
}
